import { Activity, Database, Wifi, WifiOff } from "lucide-react";

import {
  feedMode,
  feedModeLabel,
  healthStatusLabel,
  healthTone,
  providerLabel,
} from "../feedStatus";
import { formatClockTime, formatTimestamp } from "../format";
import type { ReadinessResponse } from "../types";

interface StatusStripProps {
  connected: boolean;
  lastUpdateTime: string | null;
  lastTradeTime: string | null;
  provider: string | null;
  macdVersion: string | null;
  readiness: ReadinessResponse | null;
}

export function StatusStrip({
  connected,
  lastUpdateTime,
  lastTradeTime,
  provider,
  macdVersion,
  readiness,
}: StatusStripProps) {
  const mode = feedMode(provider);
  const equitiesHealth = readiness?.feeds?.equities;
  const optionsHealth = readiness?.feeds?.options;
  const historicalHealth = readiness?.feeds?.historical;
  const analyticsHealth = readiness?.feeds?.analytics;
  const equitiesStatus = equitiesHealth?.status ?? readiness?.status;
  const equitiesTone = healthTone(equitiesStatus);
  const equitiesDisclosure = provider?.toLowerCase().includes("databento_equs")
    ? "EQUS.MINI is Databento's consolidated derived equities feed, not the official SIP NBBO"
    : null;

  return (
    <div className="status-strip" aria-label="Data feed status">
      <span className={connected ? "feed connected" : "feed disconnected"}>
        {connected ? <Wifi size={13} /> : <WifiOff size={13} />}
        {connected ? "STREAM CONNECTED" : "RECONNECTING"}
      </span>
      <span
        className={`data-mode ${mode} ${equitiesTone}`}
        title={
          [equitiesDisclosure, equitiesHealth?.detail ?? readiness?.detail]
            .filter(Boolean)
            .join(" — ") || undefined
        }
      >
        <Database size={13} />
        {providerLabel(provider)} · {feedModeLabel(provider)}
        {equitiesStatus && ` · ${healthStatusLabel(equitiesStatus)}`}
      </span>
      {optionsHealth && (
        <span
          className={`data-health ${healthTone(optionsHealth.status)}`}
          title={optionsHealth.detail ?? undefined}
        >
          OPRA · {healthStatusLabel(optionsHealth.status)}
        </span>
      )}
      {historicalHealth && (
        <span
          className={`data-health ${healthTone(historicalHealth.status)}`}
          title={historicalHealth.detail ?? undefined}
        >
          HISTORY · {healthStatusLabel(historicalHealth.status)}
        </span>
      )}
      {analyticsHealth && (
        <span
          className={`data-health ${healthTone(analyticsHealth.status)}`}
          title={analyticsHealth.detail ?? undefined}
        >
          ANALYTICS · {healthStatusLabel(analyticsHealth.status)}
        </span>
      )}
      <span title="MACD is calculated locally from stored closing prices">
        <Activity size={13} />
        MACD LOCAL · {macdVersion?.toUpperCase() ?? "EMA-V1"}
      </span>
      <div className="status-times" aria-label="Stock market timestamps">
        <span className="status-time">
          <span className="status-time-label">LAST UPDATE</span>
          <time dateTime={lastUpdateTime ?? undefined}>
            <span className="status-time-full">
              {formatTimestamp(lastUpdateTime)}
            </span>
            <span className="status-time-compact">
              {formatClockTime(lastUpdateTime)}
            </span>
          </time>
        </span>
        <span className="status-time">
          <span className="status-time-label">LAST TRADE</span>
          <time dateTime={lastTradeTime ?? undefined}>
            {lastTradeTime ? (
              <>
                <span className="status-time-full">
                  {formatTimestamp(lastTradeTime)}
                </span>
                <span className="status-time-compact">
                  {formatClockTime(lastTradeTime)}
                </span>
              </>
            ) : (
              "—"
            )}
          </time>
        </span>
      </div>
    </div>
  );
}
